/**
 * How `ui` components reach the router without depending on it. The app
 * provides these: `Link` preloads on hover and focus so the next route's
 * code and data are ready before the click, and React Aria's links navigate
 * through the router (`AriaProvider`).
 */
import { createContext, useContext } from 'react';

/** What the app provides to links. */
export interface Navigation {
  /** Starts loading the route at `href` (its chunk and loader data). */
  preload(href: string): void;
  /** Navigates to an app path without reloading the page. */
  navigate(href: string): void;
  /** The real URL for an app path. */
  href(to: string): string;
}

/** The navigation context; the default navigates by reloading. */
export const NavigationContext = createContext<Navigation>({
  preload: () => undefined,
  navigate: (href) => {
    window.location.assign(href);
  },
  href: (to) => to,
});

/** The current navigation helpers. */
export function useNavigation(): Navigation {
  return useContext(NavigationContext);
}
